import React from 'react';
import { Lead, Task, AIInsight, Opportunity } from '../../types';
import { 
  Sunrise, 
  Users2, 
  Clock4, 
  AlertTriangle, 
  TrendingUp, 
  ListChecks,
  Sparkles,
  StarIcon,
  Calendar,
  PhoneCall,
  Send,
  Bot,
  Mail,
  Star
} from 'lucide-react';

interface MorningBriefProps {
  leads?: Lead[];
  tasks?: Task[];
  opportunities?: Opportunity[];
}

const MorningBrief: React.FC<MorningBriefProps> = ({ leads = [], tasks = [], opportunities = [] }) => {
  const today = new Date();
  const yesterday = new Date(today.getTime() - 24 * 60 * 60 * 1000);
  
  // Calculate daily metrics
  const newLeadsToday = leads.filter(lead => 
    new Date(lead.createdAt).toDateString() === today.toDateString()
  );
  
  const overdueTasks = tasks.filter(task => 
    !task.completed && new Date(task.dueDate) < today
  );
  
  const todayTasks = tasks.filter(task => 
    !task.completed && new Date(task.dueDate).toDateString() === today.toDateString()
  );
  
  const highPriorityTasks = todayTasks.filter(task => task.priority === 'high');
  
  const hotLeads = leads.filter(lead => 
    lead.score >= 80 && ['interested', 'demo', 'offer', 'closing'].includes(lead.stage)
  );
  
  const insights: AIInsight[] = []; // TODO: Get from API or context
  const urgentInsights = insights.filter(insight => 
    insight.type === 'warning' || (insight.type === 'opportunity' && insight.confidence >= 85)
  );

  const callTasks = todayTasks.filter(task => task.type === 'call');
  const emailTasks = todayTasks.filter(task => task.type === 'email');
  const meetingTasks = todayTasks.filter(task => task.type === 'meeting');

  const getGreeting = () => {
    const hour = today.getHours();
    if (hour < 12) return 'Good morning';
    if (hour < 17) return 'Good afternoon';
    return 'Good evening';
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'high': return 'text-danger bg-danger-soft';
      case 'medium': return 'text-warning bg-warning-soft';
      case 'low': return 'text-success bg-success-soft';
      default: return 'text-txt-secondary bg-bg-surface';
    }
  };

  const getStageColor = (stage: string) => {
    const colors: Record<string, string> = {
      interested: 'text-accent bg-info-soft',
      demo: 'text-warning bg-warning-soft',
      offer: 'text-accent bg-accent-soft',
      closing: 'text-success bg-success-soft',
    };
    return colors[stage] || 'text-txt-secondary bg-bg-surface';
  };

  return (
    <div className="bg-bg-surface/80 rounded-xl lg:rounded-3xl shadow-card border border-default overflow-hidden backdrop-blur-sm">
      {/* Header */}
      <div className="bg-primary p-4 sm:p-6 lg:p-8 text-txt-inverted">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2 sm:space-x-3">
            <div className="p-2 sm:p-3 bg-bg-card/20 backdrop-blur-sm rounded-lg sm:rounded-xl">
              <Sunrise className="w-5 h-5 sm:w-6 sm:h-6" />
            </div>
            <div>
              <h2 className="text-lg sm:text-xl lg:text-2xl font-bold">{getGreeting()}!</h2>
              <p className="text-txt-muted text-xs sm:text-sm mt-1 hidden sm:block">
                {today.toLocaleDateString('en-US', { 
                  weekday: window.innerWidth < 640 ? 'short' : 'long', 
                  year: 'numeric', 
                  month: window.innerWidth < 640 ? 'short' : 'long', 
                  day: 'numeric' 
                })}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-1 sm:space-x-2">
            <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 text-txt-muted animate-pulse" />
            <span className="text-xs sm:text-sm text-txt-muted font-medium hidden sm:inline">AI Brief</span>
          </div>
        </div>
      </div>

      <div className="p-4 sm:p-6 lg:p-8 space-y-4 sm:space-y-6 lg:space-y-8">
        {/* Daily Summary */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 lg:gap-6">
          <div className="text-center p-3 sm:p-4 lg:p-6 bg-bg-card/90 backdrop-blur-sm rounded-2xl shadow-card border border-default/40 hover:shadow-card transition-all duration-300">
            <Users2 className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8 text-txt-secondary mx-auto mb-2" />
            <div className="text-xl sm:text-2xl lg:text-3xl font-bold text-txt">{newLeadsToday.length}</div>
            <div className="text-xs sm:text-sm text-txt-secondary font-medium">New Leads</div>
          </div>
          
          <div className="text-center p-3 sm:p-4 lg:p-6 bg-bg-card/90 backdrop-blur-sm rounded-2xl shadow-card border border-default/40 hover:shadow-card transition-all duration-300">
            <ListChecks className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8 text-txt-secondary mx-auto mb-2" />
            <div className="text-xl sm:text-2xl lg:text-3xl font-bold text-txt">{todayTasks.length}</div>
            <div className="text-xs sm:text-sm text-txt-secondary font-medium">Tasks Today</div>
          </div>
          
          <div className="text-center p-3 sm:p-4 lg:p-6 bg-bg-card/90 backdrop-blur-sm rounded-2xl shadow-card border border-default/40 hover:shadow-card transition-all duration-300">
            <StarIcon className="w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8 text-txt-secondary mx-auto mb-2" />
            <div className="text-xl sm:text-2xl lg:text-3xl font-bold text-txt">{hotLeads.length}</div>
            <div className="text-xs sm:text-sm text-txt-secondary font-medium">Hot Leads</div>
          </div>
          
          <div className="text-center p-3 sm:p-4 lg:p-6 bg-bg-card/90 backdrop-blur-sm rounded-2xl shadow-card border border-default/40 hover:shadow-card transition-all duration-300">
            <AlertTriangle className={`w-6 h-6 sm:w-7 sm:h-7 lg:w-8 lg:h-8 mx-auto mb-2 ${overdueTasks.length > 0 ? 'text-danger' : 'text-txt-muted'}`} />
            <div className={`text-xl sm:text-2xl lg:text-3xl font-bold ${overdueTasks.length > 0 ? 'text-danger' : 'text-txt-muted'}`}>
              {overdueTasks.length}
            </div>
            <div className="text-xs sm:text-sm text-txt-secondary font-medium">Overdue</div>
          </div>
        </div>

        {/* AI Summary */}
        <div className="bg-bg-card/90 backdrop-blur-sm rounded-2xl p-4 sm:p-6 shadow-card border border-default/40">
          <div className="flex items-center mb-4">
            <div className="p-2 bg-bg-surface rounded-lg lg:rounded-xl mr-3">
              <Sparkles className="w-4 h-4 sm:w-5 sm:h-5 text-txt-secondary" />
            </div>
            <h3 className="font-bold text-txt text-base sm:text-lg lg:text-xl">Daily AI Summary</h3>
          </div>
          
          <div className="prose prose-sm text-txt-secondary space-y-2 sm:space-y-3 text-xs sm:text-sm lg:text-base">
            <p>
              <strong>Today's Focus:</strong> You have <strong>{todayTasks.length} tasks</strong> scheduled
              {highPriorityTasks.length > 0 && (
                <span> with <strong className="text-danger">{highPriorityTasks.length} high-priority</strong> items</span>
              )}
              {overdueTasks.length > 0 && (
                <span> and <strong className="text-danger">{overdueTasks.length} overdue tasks</strong> requiring immediate attention</span>
              )}.
            </p>
            
            {newLeadsToday.length > 0 && (
              <p>
                <strong>New Opportunities:</strong> {newLeadsToday.length} new leads joined today, 
                including {newLeadsToday.filter(l => l.score >= 70).length} high-potential prospects.
              </p>
            )}
            
            {hotLeads.length > 0 && (
              <p>
                <strong>Hot Leads Alert:</strong> {hotLeads.length} leads are showing strong buying signals 
                and should be prioritized for follow-up.
              </p>
            )}
            
            {urgentInsights.length > 0 && (
              <p>
                <strong>AI Recommendations:</strong> {urgentInsights.length} urgent insights require your attention 
                to maximize conversion opportunities.
              </p>
            )}
          </div>
        </div>

        {/* Priority Sections */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6 lg:gap-8">
          {/* Urgent Tasks */}
          {(overdueTasks.length > 0 || highPriorityTasks.length > 0) && (
            <div className="bg-bg-card/80 backdrop-blur-sm rounded-xl lg:rounded-2xl p-4 sm:p-6 shadow-card">
              <div className="flex items-center mb-4">
                <AlertTriangle className="w-4 h-4 sm:w-5 sm:h-5 text-danger mr-2" />
                <h3 className="font-bold text-txt text-sm sm:text-base lg:text-lg">Urgent Tasks</h3>
              </div>
              
              <div className="space-y-2 sm:space-y-3 lg:space-y-4">
                {overdueTasks.slice(0, 3).map(task => (
                  <div key={task.id} className="flex flex-col sm:flex-row sm:items-center justify-between p-3 sm:p-4 bg-danger-soft rounded-lg lg:rounded-xl border-l-2 sm:border-l-4 border-danger space-y-2 sm:space-y-0">
                    <div className="flex items-center space-x-2 sm:space-x-3">
                      <div className="p-1.5 sm:p-2 bg-danger-soft rounded-lg">
                        {task.type === 'call' && <PhoneCall className="w-3 h-3 text-danger" />}
                        {task.type === 'email' && <Send className="w-3 h-3 text-danger" />}
                        {task.type === 'meeting' && <Calendar className="w-3 h-3 text-danger" />}
                        {task.type === 'follow_up' && <Clock4 className="w-3 h-3 text-danger" />}
                      </div>
                      <div>
                        <p className="font-semibold text-txt text-xs sm:text-sm line-clamp-1">{task.title}</p>
                        <p className="text-xs text-danger">Overdue by {Math.ceil((today.getTime() - new Date(task.dueDate).getTime()) / (24 * 60 * 60 * 1000))} days</p>
                      </div>
                    </div>
                    <span className={`px-2 sm:px-3 py-1 sm:py-1.5 text-xs font-semibold rounded-full ${getPriorityColor(task.priority)} self-start sm:self-center`}>
                      {task.priority}
                    </span>
                  </div>
                ))}
                
                {highPriorityTasks.slice(0, 2).map(task => (
                  <div key={task.id} className="flex items-center justify-between p-4 bg-warning-soft rounded-xl border-l-4 border-warning">
                    <div className="flex items-center space-x-3">
                      <div className="p-2 bg-warning-soft rounded-lg">
                        {task.type === 'call' && <PhoneCall className="w-3 h-3 text-warning" />}
                        {task.type === 'email' && <Send className="w-3 h-3 text-warning" />}
                        {task.type === 'meeting' && <Calendar className="w-3 h-3 text-warning" />}
                        {task.type === 'follow_up' && <Clock4 className="w-3 h-3 text-warning" />}
                      </div>
                      <div>
                        <p className="font-semibold text-txt text-sm">{task.title}</p>
                        <p className="text-xs text-txt-tertiary">Due today</p>
                      </div>
                    </div>
                    <span className="px-3 py-1.5 text-xs font-semibold bg-danger-soft text-danger rounded-full">
                      HIGH
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Hot Leads */}
          {hotLeads.length > 0 && (
            <div className="bg-bg-card/80 backdrop-blur-sm rounded-xl lg:rounded-2xl p-4 sm:p-6 shadow-card">
              <div className="flex items-center mb-4">
                <TrendingUp className="w-4 h-4 sm:w-5 sm:h-5 text-success mr-2" />
                <h3 className="font-bold text-txt text-sm sm:text-base lg:text-lg">Hot Leads</h3>
              </div>
              
              <div className="space-y-2 sm:space-y-3 lg:space-y-4">
                {hotLeads.slice(0, 4).map(lead => (
                  <div key={lead.id} className="flex flex-col sm:flex-row sm:items-center justify-between p-3 sm:p-4 bg-success-soft rounded-lg lg:rounded-xl border-l-2 sm:border-l-4 border-success space-y-2 sm:space-y-0">
                    <div>
                      <p className="font-semibold text-txt text-xs sm:text-sm">{lead.firstName} {lead.lastName}</p>
                      <p className="text-xs text-txt-secondary truncate">{lead.company}</p>
                    </div>
                    <div className="flex items-center space-x-1 sm:space-x-2 self-start sm:self-center">
                      <span className={`px-2 sm:px-3 py-1 sm:py-1.5 text-xs font-semibold rounded-full ${getStageColor(lead.stage)}`}>
                        {lead.stage}
                      </span>
                      <span className="text-success font-bold text-xs sm:text-sm bg-success-soft px-2 py-1 rounded-full">{lead.score}%</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Today's Schedule */}
        <div className="bg-bg-card/80 backdrop-blur-sm rounded-xl lg:rounded-2xl p-4 sm:p-6 shadow-card">
          <div className="flex items-center mb-4">
            <Calendar className="w-4 h-4 sm:w-5 sm:h-5 text-accent mr-2" />
            <h3 className="font-bold text-txt text-sm sm:text-base lg:text-lg">Today's Schedule</h3>
          </div>
          
          <div className="grid grid-cols-3 gap-3 sm:gap-4 lg:gap-6">
            <div className="text-center p-3 sm:p-4 lg:p-6 bg-info-soft rounded-xl lg:rounded-2xl hover:bg-info-soft transition-colors duration-200">
              <PhoneCall className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-accent mx-auto mb-2" />
              <div className="text-lg sm:text-xl lg:text-2xl font-bold text-accent">{callTasks.length}</div>
              <div className="text-xs sm:text-sm text-txt-secondary font-medium">Calls</div>
            </div>
            
            <div className="text-center p-3 sm:p-4 lg:p-6 bg-success-soft rounded-xl lg:rounded-2xl hover:bg-success-soft transition-colors duration-200">
              <Send className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-success mx-auto mb-2" />
              <div className="text-lg sm:text-xl lg:text-2xl font-bold text-success">{emailTasks.length}</div>
              <div className="text-xs sm:text-sm text-txt-secondary font-medium">Emails</div>
            </div>
            
            <div className="text-center p-3 sm:p-4 lg:p-6 bg-accent-soft rounded-xl lg:rounded-2xl hover:bg-accent-soft transition-colors duration-200">
              <Calendar className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 text-accent mx-auto mb-2" />
              <div className="text-lg sm:text-xl lg:text-2xl font-bold text-accent">{meetingTasks.length}</div>
              <div className="text-xs sm:text-sm text-txt-secondary font-medium">Meetings</div>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-bg-card/80 backdrop-blur-sm rounded-xl lg:rounded-2xl p-4 sm:p-6 shadow-card">
          <h3 className="font-bold text-txt mb-4 sm:mb-6 text-sm sm:text-base lg:text-lg">Suggested Actions</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
            {overdueTasks.length > 0 && (
              <button className="flex items-center justify-center px-4 sm:px-6 py-3 sm:py-4 bg-primary text-primary-contrast rounded-lg lg:rounded-xl hover:bg-bg-card hover:scale-105 transition-all duration-200 shadow-card text-sm lg:text-base">
                <AlertTriangle className="w-4 h-4 mr-1 sm:mr-2" />
                Review {overdueTasks.length} Overdue Tasks
              </button>
            )}
            
            {newLeadsToday.length > 0 && (
              <button className="flex items-center justify-center px-4 sm:px-6 py-3 sm:py-4 bg-primary text-primary-contrast rounded-lg lg:rounded-xl hover:bg-bg-card hover:scale-105 transition-all duration-200 shadow-card text-sm lg:text-base">
                <Users2 className="w-4 h-4 mr-1 sm:mr-2" />
                Process {newLeadsToday.length} New Leads
              </button>
            )}
            
            {hotLeads.length > 0 && (
              <button className="flex items-center justify-center px-4 sm:px-6 py-3 sm:py-4 bg-primary text-primary-contrast rounded-lg lg:rounded-xl hover:bg-bg-card hover:scale-105 transition-all duration-200 shadow-card text-sm lg:text-base">
                <StarIcon className="w-4 h-4 mr-1 sm:mr-2" />
                Follow Up Hot Leads
              </button>
            )}
            
            <button className="flex items-center justify-center px-4 sm:px-6 py-3 sm:py-4 bg-primary text-primary-contrast rounded-lg lg:rounded-xl hover:bg-bg-card hover:scale-105 transition-all duration-200 shadow-card text-sm lg:text-base sm:col-span-2 lg:col-span-1">
              <Bot className="w-4 h-4 mr-1 sm:mr-2" />
              View AI Insights
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MorningBrief;